import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";

// Falls back to the relative "/api" (same-origin) when VITE_API_BASE_URL is not
// baked in — correct on any deployed host (nginx proxies /api to the backend),
// with no CORS. For a bare local `npm run dev`, set VITE_API_BASE_URL in .env.
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL?.trim() || "/api";

const AUTH_TOKEN_KEY = "auth_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const REFRESH_PATH = "/v1/auth/refresh/";

// Create axios instance with base configuration
export const apiClient = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
  // Serialize array query params as repeated keys (?status=a&status=b) rather
  // than axios's default bracketed form (?status[]=a&status[]=b). Django/DRF
  // read repeated keys via request.query_params.getlist(); the bracketed form
  // is not recognized, so multi-value filters (e.g. the multi-status combobox)
  // would silently return nothing. Applies to every apiClient request.
  paramsSerializer: { indexes: null },
});

// Internal request config extended with a one-shot retry marker. The flag
// guarantees a request is retried at most once after a silent token refresh,
// preventing infinite refresh/retry loops.
interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// When sending FormData, remove the default Content-Type so axios sets
// multipart/form-data with the correct boundary automatically.
apiClient.interceptors.request.use((config) => {
  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }
  return config;
});

// Request interceptor for adding auth tokens, etc.
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * Clears both tokens and redirects to the login page (unless already there,
 * to avoid an infinite redirect loop).
 *
 * Public, intentionally-unauthenticated routes (e.g. the customer tracking page
 * at `/track/:token`) must NEVER be bounced to /login: a recipient may open the
 * link while a stale `auth_token` from an old dispatcher session sits in
 * localStorage, and a 401 from the bootstrap `/auth/me` would otherwise yank
 * them off the public page. On those routes we still clear the dead tokens but
 * skip the redirect.
 */
const PUBLIC_PATH_PREFIXES = ["/track/"];

function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATH_PREFIXES.some((p) => pathname.startsWith(p));
}

/**
 * sessionStorage flag meaning "the last trip to /login was an involuntary session expiry".
 *
 * TASK-4738: expiry used to be entirely silent — `forceLogout()` was a bare redirect, so a user
 * whose token died mid-task landed on the login screen with no explanation, indistinguishable
 * from having clicked "Sign out". The flag is set ONLY here, on the expiry path. A deliberate
 * logout goes through the auth store's `logout()` and an in-app `navigate("/login")`, never
 * through this function, so it cannot raise the notice.
 *
 * sessionStorage (not localStorage) so the notice dies with the tab and can never resurface in
 * a later session; and it is consumed — read-then-removed — so a manual reload of /login does
 * not show a stale "your session expired" banner.
 */
export const SESSION_EXPIRED_KEY = "session_expired";

/** Records that the session ended involuntarily. Safe in environments with no sessionStorage. */
export function markSessionExpired(): void {
  try {
    window.sessionStorage.setItem(SESSION_EXPIRED_KEY, "1");
  } catch {
    // Private-mode or a storage-disabled browser: the notice is a nicety, never a blocker.
  }
}

/**
 * Returns whether the last redirect to /login was an expiry, and clears the flag so the
 * message is shown exactly once.
 */
export function consumeSessionExpired(): boolean {
  try {
    const flagged = window.sessionStorage.getItem(SESSION_EXPIRED_KEY) === "1";
    if (flagged) window.sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    return flagged;
  } catch {
    return false;
  }
}

function forceLogout(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  if (
    window.location.pathname !== "/login" &&
    !isPublicPath(window.location.pathname)
  ) {
    // Flag BEFORE navigating: the assignment to `location.href` may unload the document
    // immediately, and anything queued after it is not guaranteed to run.
    markSessionExpired();
    window.location.href = "/login";
  }
}

// --- Silent refresh single-flight machinery -------------------------------
// While one refresh request is in flight, concurrent 401s must not each fire
// their own refresh. They await the same shared promise and, once it resolves
// with a fresh access token, replay their original request.
let refreshPromise: Promise<string> | null = null;

/**
 * Performs the token refresh exactly once at a time. A dedicated bare axios
 * call (not apiClient) is used so the request bypasses this response
 * interceptor and cannot recurse on its own 401.
 *
 * EXPORTED for the WebSocket hooks (TASK-4987 / BUG-580). A socket closed with
 * 4401 has an expired access token in its query string and must refresh before
 * reconnecting; it MUST reuse this function rather than write a second refresh
 * implementation, because the `refreshPromise` single-flight above is what stops
 * three hooks (tracking, notifications, chat) all firing their own refresh at
 * the same moment. Two independent implementations would defeat it.
 */
export function refreshAccessToken(): Promise<string> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const refresh = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refresh) {
      throw new Error("No refresh token available");
    }

    const response = await axios.post<{ access: string; refresh?: string }>(
      `${apiBaseUrl}${REFRESH_PATH}`,
      { refresh },
      {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      },
    );

    const { access, refresh: rotatedRefresh } = response.data;
    localStorage.setItem(AUTH_TOKEN_KEY, access);
    // ROTATE_REFRESH_TOKENS is disabled on the backend, so `refresh` is
    // normally absent; persist it defensively if rotation is ever enabled.
    if (rotatedRefresh) {
      localStorage.setItem(REFRESH_TOKEN_KEY, rotatedRefresh);
    }
    // Keep the default Authorization header in sync for subsequent requests.
    apiClient.defaults.headers.common.Authorization = `Bearer ${access}`;
    return access;
  })();

  // Always clear the shared promise once settled so the next 401 can refresh
  // again, regardless of success or failure. Capture a local handle so the
  // clean-up chain operates on this exact promise, and attach a no-op catch to
  // the clean-up chain only (the original promise is still returned and its
  // rejection is handled by callers) to avoid an unhandled rejection warning.
  const current = refreshPromise;
  current
    .catch(() => undefined)
    .finally(() => {
      if (refreshPromise === current) {
        refreshPromise = null;
      }
    });

  return current;
}

// Response interceptor: silent refresh + retry on 401.
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalConfig = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    const isRefreshRequest = originalConfig?.url?.includes(REFRESH_PATH);

    // Only attempt a silent refresh for genuine access-token expiry: a 401 on
    // a real request that is not the refresh call and has not been retried yet.
    if (
      status === 401 &&
      originalConfig &&
      !originalConfig._retry &&
      !isRefreshRequest
    ) {
      // No refresh token at all — nothing to recover with.
      if (!localStorage.getItem(REFRESH_TOKEN_KEY)) {
        forceLogout();
        return Promise.reject(error);
      }

      originalConfig._retry = true;

      try {
        const newAccess = await refreshAccessToken();
        // Update the retried request with the fresh token and replay it once.
        originalConfig.headers.Authorization = `Bearer ${newAccess}`;
        return apiClient(originalConfig as AxiosRequestConfig);
      } catch (refreshError) {
        // Refresh token is invalid/expired or the endpoint errored — only now
        // do we log the user out.
        forceLogout();
        return Promise.reject(refreshError);
      }
    }

    // A 401 on the refresh request itself means the refresh token is dead.
    if (status === 401 && isRefreshRequest) {
      forceLogout();
    }

    return Promise.reject(error);
  },
);

// Example API functions
export const healthCheck = async () => {
  const response = await apiClient.get("/health/");
  return response.data;
};
